import _ from "lodash";
import { Sentry } from "services/sentry";
import { ApolloLink, ApolloClient } from "@apollo/client";
import { getOperationName, cacheSizes, print } from "@apollo/client/utilities";
import { enableApolloClientDevTools, enableInputSanitization } from "config";

// Import from accessor module
import { setApolloClient } from "./apolloClientAccessor";

// middleWares
import {
  errorLink,
  networkLink,
  authLink,
  lsnLink,
  sanitizeLink,
} from "./Middlewares";
// cache
import { cache } from "./Cache";

//TODO: Can we separate error handling part from ApolloClientWrapper and InMemoryCacheWrapper?
class ApolloClientWrapper extends ApolloClient {
  constructor(options) {
    super(options);
  }

  readQuery(...options) {
    /**
     * How to use debug parameter
     * -> client.readQuery({
            query: guidanceDocumentsQuery,
            variables,
            debug: true
            })
     */

    const {
      debug = false,
      query,
      variables,
      logMissingDataWarning = false,
    } = _.first(options);

    const response = super.readQuery(...options);

    if (debug && response === null) {
      // eslint-disable-next-line no-console
      console.log(
        "Query",
        getOperationName(query),
        _.cloneDeep({
          queryData: this?.cache?.diff({
            query,
            variables,
          }),
        })
      );
    }

    /**
     * How to use logMissingDataWarning parameter
     * -> client.readQuery({
            query: guidanceDocumentsQuery,
            variables,
            logMissingDataWarning: true
            })
     */
    if (logMissingDataWarning && response === null) {
      try {
        const operationName = getOperationName(query);
        Sentry.captureMessage("MISSING_DATA_WARNING", {
          level: "info",
          extra: {
            operationName,
            variables: JSON.stringify(variables, null, 2),
            queryData: JSON.stringify(
              this?.cache?.diff({
                query,
                variables,
              }),
              null,
              2
            ),
          },
        });
      } catch (e) {}
    }

    return response;
  }

  /**
   * Aim of overriding watchQuery method is to get instance of ObservableQuery to fix some issues which are open in apollo client.
   * DO NOT MODIFY ANYTHING IN THIS METHOD WITHOUT DISCUSSION. All changes are made considering that better solution is not possible to fix these issues.
   */
  watchQuery(...options) {
    const observableQuery = super.watchQuery(...options);

    /**
     * Problem:
     * In apollo client v3, cache is more reactive than its predecessor. It can cause bad consequences, in certain scenarios.
     * So, we need to control it to stop broadcasting of cache updates.
     *
     * Solution:
     * When to render a component on cache updates is controlled by shouldNotify method of QueryInfo class.
     * Since we can't access and modify QueryInfo class, we are using a hack to access its instance from the ObservableQuery instance returned by watchQuery method.
     * Here, we are assigning a new function to shouldNotify method of queryInfo, which can execute custom logic to prevent component rendering.
     * Original implementation of shouldNotify: https://github.dev/apollographql/apollo-client/blob/69563f157b54a0cd23958a609bdf9e016a1a25e1/src/core/QueryInfo.ts#L268
     */
    if (observableQuery.queryInfo) {
      const queryInfo = observableQuery.queryInfo;

      const originalShouldNotify = queryInfo.shouldNotify.bind(queryInfo);

      queryInfo.shouldNotify = function (...args) {
        /**
         * Issue:
         * According to the current implementation of QueryInfo class, if request for 'QueryA' is in progress and cache for QueryA is updated due to some other queries/mutations,
         * then component using 'QueryA' will be reredered with networkStatus as 7. This can happen when fetchPolicy is cache-and-network or cache-only.
         * This can cause issues, while pagination request is active as component will receive networkStatus as 7, which can cause multiple duplicate paginated requests.
         *
         * Solution:
         * We are blocking component rerendering in middle of a pagination request when fetchPolicy is cache-and-network.
         **/
        if (
          this.observableQuery &&
          this.observableQuery.options.fetchPolicy === "cache-and-network" &&
          this.networkStatus === 3
        ) {
          // Uncomment below code to see how many times this hack is preventing rendering of component in middle of a paginated request.
          // To see the logs for particular requests only, use its variables such that below condition is satisfied only for those requests.

          // if (this.observableQuery.options.variables?.filters?.first) {
          //   console.log(
          //     "%c Prevented rendering in middle of a request",
          //     "color:red;font-size:20px;"
          //   );
          // }
          return false;
        }

        /**
         * Issue:
         * In apollo client v3, whenever cache is updated with incomplete data, it rerenders corresponding components.
         * This is problematic as it is difficult to make sure that every query is updated with complete data in the cache.
         *
         * Solution:
         * Ideally, this can be solved by "nextFetchPolicy", but since it is buggy, we can't rely on it.
         * So, we are adding a HACK to handle this case. Here, "dirty" is a flag which is set to true whenever cache is updated and
         * "complete" is a flag which is set to false whenever cache is updated with incomplete data.
         */
        const dirty = this.dirty;
        const complete = _.get(this, "lastDiff.diff.complete", true);

        if (dirty && !complete) {
          const query = _.get(
            this,
            "lastDiff.options.query.loc.source.body",
            ""
          );
          const optimistic = _.get(this, "lastDiff.options.optimistic", false);

          console.warn(
            `Due to ${
              optimistic ? "optimistic" : ""
            } cache update, cache has incomplete data for the following query`
          );
          console.warn(`Query: ${query}`);

          console.warn(
            `Status of missing cached data for above query::`,
            _.get(this, "lastDiff.diff.missing")
          );

          return false;
        }

        return originalShouldNotify(...args);
      };
    }

    if (observableQuery.fetchMore) {
      const originalFetchMore = observableQuery.fetchMore.bind(observableQuery);

      /**
       * Issue: While pagination request for query 'QueryA' is active, if same query is triggered with different variables('QueryB'),
       * then when response of 'QueryA' is received, it is merged with the cached data corresponding to the 'QueryB', which should not happen.
       * This is likely to happen where we support paginated query with filters.
       *
       * Solution: Issue is happening because variables of Observable query are mutated, when same query is triggered with different variables.
       * So, we are storing original variables in a separate variable and then comparing it with current variables in updateQuery method to decide whether to update cache or not.
       * Solution is inspired after analyzing the source code of apollo client: https://github.dev/apollographql/apollo-client/blob/e9fd314f325300d7c5f979fbef719aee498481b2/src/core/ObservableQuery.ts#L503
       *
       * Note: This issue does not happen for normal queries. It is specific to pagination.
       */
      observableQuery.fetchMore = function (...args) {
        const that = this;
        const variables = this.variables;

        const [fetchMoreArguments] = args;

        if (fetchMoreArguments?.updateQuery) {
          const originalUpdateQuery = fetchMoreArguments.updateQuery.bind(
            fetchMoreArguments
          );

          fetchMoreArguments.updateQuery = function (...args) {
            if (!_.isEqual(that.variables, variables)) {
              return;
            }

            return originalUpdateQuery(...args);
          };
        }

        return originalFetchMore(...args);
      };
    }

    return observableQuery;
  }
}

const createLinkChain = () => {
  const links = [authLink, lsnLink, errorLink, networkLink];

  if (enableInputSanitization) {
    // If input sanitization is enabled, add the sanitize link to the chain
    links.splice(1, 0, sanitizeLink);
  }

  return ApolloLink.from(links);
};

// Create the Apollo client instance
const client = new ApolloClientWrapper({
  link: createLinkChain(),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
  connectToDevTools: enableApolloClientDevTools,
});

// Register the client with the accessor module
setApolloClient(client);

// Configure cache sizes
cacheSizes["inMemoryCache.executeSelectionSet"] = 150000;
cacheSizes["inMemoryCache.executeSubSelectedArray"] = 30000;
cacheSizes["inMemoryCache.maybeBroadcastWatch"] = 10000;

print.reset();

export default client;
